import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, TextField, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Stack, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Tooltip,
  InputAdornment, Chip, Skeleton, Alert, Snackbar, Switch, FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import * as actions from '../../redux/actions';

const EMPTY_FORM = {
  name: '',
  category: '',
  description: '',
  default_unit: 'JOB',
  default_price: 0,
  is_active: true,
};

export default function ItemTemplateList() {
  const dispatch = useDispatch();
  const { rows = [], isLoading } = useSelector((s) => s.itemTemplate);

  const [q, setQ] = useState('');
  const [dialog, setDialog] = useState({ open: false, mode: 'create', item: null });
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });
  const [nameTouched, setNameTouched] = useState(false);

  useEffect(() => {
    dispatch(actions.itemTemplateAll({ q: '', limit: 200 }));
  }, [dispatch]);

  const filtered = rows.filter((t) =>
    !q ||
    t.name?.toLowerCase().includes(q.toLowerCase()) ||
    t.category?.toLowerCase().includes(q.toLowerCase()) ||
    t.description?.toLowerCase().includes(q.toLowerCase()),
  );

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setNameTouched(false);
    setDialog({ open: true, mode: 'create', item: null });
  };

  const openEdit = (item) => {
    setForm({
      name: item.name || '',
      category: item.category || '',
      description: item.description || '',
      default_unit: item.default_unit || 'JOB',
      default_price: item.default_price || 0,
      is_active: item.is_active !== false,
    });
    setNameTouched(false);
    setDialog({ open: true, mode: 'edit', item });
  };

  const closeDialog = () => setDialog({ open: false, mode: 'create', item: null });

  const handleSave = async () => {
    setNameTouched(true);
    if (!form.name?.trim()) return;
    setSaving(true);
    try {
      if (dialog.mode === 'create') {
        await dispatch(actions.itemTemplateCreate(form));
        showSnack('สร้างแม่แบบสำเร็จ');
      } else {
        await dispatch(actions.itemTemplateUpdate(dialog.item._id, form));
        showSnack('แก้ไขแม่แบบสำเร็จ');
      }
      dispatch(actions.itemTemplateAll({ q: '', limit: 200 }));
      closeDialog();
    } catch (err) {
      showSnack(err.message || 'เกิดข้อผิดพลาด', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await dispatch(actions.itemTemplateDelete(deleteTarget._id));
      showSnack('ลบแม่แบบสำเร็จ');
      dispatch(actions.itemTemplateAll({ q: '', limit: 200 }));
    } catch (err) {
      showSnack(err.message || 'เกิดข้อผิดพลาด', 'error');
    } finally {
      setSaving(false);
      setDeleteTarget(null);
    }
  };

  const showSnack = (msg, severity = 'success') => {
    setSnack({ open: true, msg, severity });
  };

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>แม่แบบรายการ</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreate}
          sx={{ borderRadius: 2 }}
        >
          เพิ่มแม่แบบ
        </Button>
      </Stack>

      <Paper variant="outlined" sx={{ borderRadius: 3 }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <TextField
            size="small"
            placeholder="ค้นหาชื่อ, หมวดหมู่, รายละเอียด..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
              ),
            }}
            sx={{ minWidth: 280, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 700 }}>ชื่อ</TableCell>
                <TableCell sx={{ fontWeight: 700, width: 120 }}>หมวดหมู่</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>รายละเอียด</TableCell>
                <TableCell sx={{ fontWeight: 700, width: 80 }}>หน่วย</TableCell>
                <TableCell sx={{ fontWeight: 700, width: 120, textAlign: 'right' }}>ราคาเริ่มต้น</TableCell>
                <TableCell sx={{ fontWeight: 700, width: 90, textAlign: 'center' }}>สถานะ</TableCell>
                <TableCell sx={{ width: 80 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading
                ? Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 7 }).map((__, j) => (
                        <TableCell key={j}><Skeleton variant="text" /></TableCell>
                      ))}
                    </TableRow>
                  ))
                : filtered.length === 0
                ? (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                        ไม่พบแม่แบบ
                      </TableCell>
                    </TableRow>
                  )
                : filtered.map((item) => (
                    <TableRow key={item._id} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{item.name}</TableCell>
                      <TableCell>
                        {item.category && (
                          <Chip label={item.category} size="small" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-line', maxWidth: 300 }} noWrap>
                          {item.description || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>{item.default_unit || '-'}</TableCell>
                      <TableCell sx={{ textAlign: 'right', fontWeight: 600 }}>
                        {Number(item.default_price || 0).toLocaleString('th-TH', {
                          minimumFractionDigits: 2,
                        })}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Chip
                          size="small"
                          label={item.is_active !== false ? 'ใช้งาน' : 'ปิด'}
                          color={item.is_active !== false ? 'success' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5}>
                          <Tooltip title="แก้ไข">
                            <IconButton size="small" onClick={() => openEdit(item)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="ลบ">
                            <IconButton size="small" color="error" onClick={() => setDeleteTarget(item)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
              }
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create / Edit dialog */}
      <Dialog
        open={dialog.open}
        onClose={closeDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle fontWeight={700}>
          {dialog.mode === 'create' ? 'เพิ่มแม่แบบใหม่' : 'แก้ไขแม่แบบ'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <TextField
              fullWidth
              label="ชื่อแม่แบบ *"
              size="small"
              value={form.name}
              onChange={(e) => setField('name', e.target.value)}
              onBlur={() => setNameTouched(true)}
              error={nameTouched && !form.name?.trim()}
              helperText={nameTouched && !form.name?.trim() ? 'กรุณาระบุชื่อ' : ''}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <TextField
              fullWidth
              label="หมวดหมู่"
              size="small"
              value={form.category}
              onChange={(e) => setField('category', e.target.value)}
              placeholder="เช่น งานระบบ, งานไฟฟ้า"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <TextField
              fullWidth
              label="รายละเอียด"
              size="small"
              multiline
              rows={3}
              value={form.description}
              onChange={(e) => setField('description', e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="หน่วยเริ่มต้น"
                size="small"
                value={form.default_unit}
                onChange={(e) => setField('default_unit', e.target.value)}
                placeholder="JOB"
                sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
              <TextField
                label="ราคาเริ่มต้น"
                type="number"
                size="small"
                value={form.default_price}
                onChange={(e) => setField('default_price', parseFloat(e.target.value) || 0)}
                inputProps={{ min: 0, step: 'any' }}
                sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Stack>
            <FormControlLabel
              control={
                <Switch
                  checked={form.is_active}
                  onChange={(e) => setField('is_active', e.target.checked)}
                />
              }
              label="เปิดใช้งาน"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={closeDialog} variant="outlined" sx={{ borderRadius: 2 }}>ยกเลิก</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || !form.name?.trim()}
            sx={{ borderRadius: 2 }}
          >
            {saving ? 'กำลังบันทึก...' : 'บันทึก'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm dialog */}
      <Dialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle fontWeight={700}>ยืนยันการลบ</DialogTitle>
        <DialogContent>
          <Typography>
            ต้องการลบแม่แบบ <b>{deleteTarget?.name}</b> ใช่หรือไม่?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} variant="outlined" sx={{ borderRadius: 2 }}>
            ยกเลิก
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            disabled={saving}
            sx={{ borderRadius: 2 }}
          >
            ลบ
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((p) => ({ ...p, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
